class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    // ✅ update progress bar on open
    scheduleFreeShippingRefresh(150);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // ✅ update progress bar after render
    scheduleFreeShippingRefresh(200);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

/* ============================================================
   Cart Drawer Items
============================================================ */
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  // Runs after qty update (plus/minus)
  onCartUpdate() {
    super.onCartUpdate();
    scheduleFreeShippingRefresh(200);
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ============================================================
   Free Shipping Progress Bar Logic
============================================================ */
function formatMoney(cents) {
  if (!cents) cents = 0;

  let value = (cents / 100).toFixed(2);

  // Remove .00 if you want clean money like $80 instead of $80.00
  value = value.replace('.00', '');

  return `$${value}`;
}

function updateFreeShippingProgressBar(cartTotal) {
  const barWrapper = document.querySelector('.cart-drawer-shipping-goal');
  if (!barWrapper) return;

  const goal = parseInt(barWrapper.dataset.goal, 10);
  if (!goal || goal <= 0) return;

  const textEl = barWrapper.querySelector('.cart-drawer-shipping-text');
  const fillEl = barWrapper.querySelector('.cart-drawer-shipping-bar-fill');

  if (!textEl || !fillEl) return;

  const total = parseInt(cartTotal || 0, 10);
  const remaining = goal - total;

  let progressPercent = (total / goal) * 100;
  progressPercent = Math.max(0, Math.min(progressPercent, 100));

  fillEl.style.width = progressPercent + '%';

  if (remaining > 0) {
    textEl.innerHTML = `Spend another <strong>${formatMoney(remaining)}</strong> and get free shipping`;
  } else {
    textEl.innerHTML = `<strong>You’ve unlocked free shipping!</strong>`;
  }
}

function refreshFreeShippingProgressBar() {
  const barWrapper = document.querySelector('.cart-drawer-shipping-goal');
  if (!barWrapper) return;

  fetch('/cart.js')
    .then((response) => response.json())
    .then((cart) => {
      updateFreeShippingProgressBar(cart.total_price || 0);
    })
    .catch(() => {});
}

let freeShippingRefreshTimeout = null;

function scheduleFreeShippingRefresh(delay = 150) {
  clearTimeout(freeShippingRefreshTimeout);

  freeShippingRefreshTimeout = setTimeout(() => {
    refreshFreeShippingProgressBar();
  }, delay);
}

/* ============================================================
   Observer (Fixes Qty Update Issue)
============================================================ */
function initFreeShippingObserver() {
  const cartDrawer = document.querySelector('#CartDrawer');
  if (!cartDrawer || cartDrawer.dataset.freeShippingObserverReady === 'true') return;

  cartDrawer.dataset.freeShippingObserverReady = 'true';

  const observer = new MutationObserver(() => {
    scheduleFreeShippingRefresh(150);
  });

  observer.observe(cartDrawer, {
    childList: true,
    subtree: true,
    characterData: true,
  });
}

/* ============================================================
   Init Events
============================================================ */
document.addEventListener('DOMContentLoaded', function () {
  initFreeShippingObserver();
  scheduleFreeShippingRefresh(200);
});

document.addEventListener('shopify:section:load', function () {
  initFreeShippingObserver();
  scheduleFreeShippingRefresh(200);
});

// Detect qty clicks inside cart drawer
document.addEventListener('click', function (event) {
  if (!event.target.closest('cart-drawer')) return;

  if (
    event.target.closest('.quantity__button') ||
    event.target.closest('cart-remove-button')
  ) {
    scheduleFreeShippingRefresh(500);
  }
});

// Detect qty input manual change
document.addEventListener('change', function (event) {
  if (!event.target.closest('cart-drawer')) return;

  if (event.target.matches('.quantity__input')) {
    scheduleFreeShippingRefresh(500);
  }
});